import axios from 'axios';
import axiosRetry from 'axios-retry';
import { baseUrl, doWebSocketServiceHost } from '../constants/AppConstants';
import { errorLogger } from './trackingUtil';

const RETRY_COUNT = 2;
export const APIRequest = axios.create({
  withCredentials: true,

  /* Refer: https://docs.djangoproject.com/en/1.8/ref/csrf/#ajax
     `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
  */
  // Todo: Move value to constants file
  xsrfCookieName: 'doselectcsrf',

  /* Refer: https://docs.djangoproject.com/en/1.8/ref/csrf/#ajax
     `xsrfHeaderName` is the name of the http header that carries the xsrf token value
  */
  // Todo: Move value to constants file
  xsrfHeaderName: 'X-CSRFToken',
});
// we are attaching axios retry only to RetryAPIRequest, So that retry event is not attached to other api calls for which it is not necessary.
export const RetryAPIRequest = axios.create({
  withCredentials: true,
  xsrfCookieName: 'doselectcsrf',
  xsrfHeaderName: 'X-CSRFToken',
});

APIRequest.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // catch the error and send it to sentry
    // captureException(error);
    errorLogger(error);
    return Promise.reject(error);
  }
);

RetryAPIRequest.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    errorLogger(error);
    return Promise.reject(error);
  }
);
const setRetry = retryCallback => {
  axiosRetry(RetryAPIRequest, {
    retries: RETRY_COUNT,
    shouldResetTimeout: true,
    onRetry: (retryCount, error, requestConfig) => {
      retryCallback(retryCount);
      errorLogger(error, {
        type: 'Retry CORS/Network Err',
        retryCount,
        requestConfig,
        bandwidth: navigator?.connection?.effectiveType,
      });
    },
    retryCondition: error => {
      const method = error?.config?.method;
      const isNetworkError =
        (!error.response && !!error.request) ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ENOTFOUND';
      return isNetworkError && method === 'patch';
    },
  });
};
const webSocketAPIRequest = axios.create({
  withCredentials: true,
});

const APIRequestNoCredentials = axios.create({
  withCredentials: false,
});

export const env = 'PLT';

let userName = '';
/**
 * Function is used to set the user name so that it can be appended on every API call
 * @param {String} name
 */
export const setUserName = name => {
  userName = name;
};

webSocketAPIRequest.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    errorLogger(error);
    return Promise.reject(error);
  }
);

APIRequestNoCredentials.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    errorLogger(error);
    return Promise.reject(error);
  }
);

export const makeProxyRequest = (method, url, appendUserName, data) => {
  try {
    let params = '';
    if (appendUserName) {
      if (url && url.includes('?')) params = `&__env=${env}&__user=${userName}`;
      else params = `?__env=${env}&__user=${userName}`;
    }
    return APIRequest({
      method,
      url: `${baseUrl}${url}${params}`,
      data,
    });
  } catch (err) {
    return Promise.reject(err);
  }
};

export const makeRetryProxyRequest = (
  method,
  url,
  appendUserName,
  data,
  retryCallback,
  options
) => {
  try {
    let params = '';
    if (appendUserName) {
      if (url && url.includes('?')) params = `&__env=${env}&__user=${userName}`;
      else params = `?__env=${env}&__user=${userName}`;
    }
    if (retryCallback) {
      setRetry(retryCallback);
    }
    return RetryAPIRequest({
      method,
      url: `${baseUrl}${url}${params}`,
      data,
      ...(options || {}),
    });
  } catch (err) {
    return Promise.reject(err);
  }
};

export const makeRequest = (method, url, data) => {
  try {
    return APIRequest({
      method,
      url: `${baseUrl}${url}`,
      data,
    });
  } catch (err) {
    return Promise.reject(err);
  }
};

export const makeRequestWithoutBaseUrl = (method, url, data) => {
  try {
    return APIRequestNoCredentials({
      method,
      url,
      data,
    });
  } catch (err) {
    return Promise.reject(err);
  }
};

export const makeWebSocketServiceRequest = (method, url) => {
  try {
    return webSocketAPIRequest({
      method,
      url: `${doWebSocketServiceHost}${url}`,
    });
  } catch (err) {
    return Promise.reject(err);
  }
};

const APIRequestWithExpectedErrors = axios.create({
  withCredentials: true,

  /* Refer: https://docs.djangoproject.com/en/1.8/ref/csrf/#ajax
     `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
  */
  // Todo: Move value to constants file
  xsrfCookieName: 'doselectcsrf',

  /* Refer: https://docs.djangoproject.com/en/1.8/ref/csrf/#ajax
     `xsrfHeaderName` is the name of the http header that carries the xsrf token value
  */
  // Todo: Move value to constants file
  xsrfHeaderName: 'X-CSRFToken',
});

export const makeProxyRequestWithExpectedErrors = async ({
  expectedError,
  method,
  url,
  appendUserName,
  data,
}) => {
  try {
    let params = '';
    if (appendUserName) {
      if (url && url.includes('?')) params = `&__env=${env}&__user=${userName}`;
      else params = `?__env=${env}&__user=${userName}`;
    }

    try {
      const resp = await APIRequestWithExpectedErrors({
        method,
        url: `${baseUrl}${url}${params}`,
        data,
      });
      return resp;
    } catch (error) {
      if (error.response.status !== expectedError) {
        errorLogger(error);
      }
      return Promise.reject(error);
    }
  } catch (err) {
    return Promise.reject(err);
  }
};
